import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";
import { useToast } from "../lib/toast";
import type { Fulfillment, PaymentMethod } from "../lib/types";

const fmt = (n: number) => n.toLocaleString("ko-KR");

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const nav = useNavigate();
  const { push } = useToast();
  const [fulfillment, setFulfillment] = useState<Fulfillment>("delivery");
  const [payment, setPayment] = useState<PaymentMethod>("card");
  const [form, setForm] = useState({
    ship_name: "", ship_phone: "", ship_address: "", ship_memo: "",
    pickup_site: "", pickup_date: "",
  });
  const [taxInvoice, setTaxInvoice] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const defaultShipping = fulfillment === "delivery" && subtotal < 300_000 ? 10_000 : 0;
  const [shippingOverride, setShippingOverride] = useState<number | null>(null);
  const shipping = shippingOverride ?? defaultShipping;
  const total = subtotal + shipping;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const res = await api.post<{ ok: true; order_id: string; order_no: string }>("/orders", {
        items: items.map((it) => ({ product_id: it.product.id, quantity: it.quantity })),
        fulfillment, payment_method: payment, shipping_fee: shipping,
        ship_name: form.ship_name || undefined,
        ship_phone: form.ship_phone || undefined,
        ship_address: form.ship_address || undefined,
        ship_memo: form.ship_memo || undefined,
        pickup_site: form.pickup_site || undefined,
        pickup_date: form.pickup_date || undefined,
        tax_invoice_requested: taxInvoice,
      });
      clear();
      push(`주문이 접수되었습니다 · ${res.order_no}`, "success");
      nav(`/orders/${res.order_id}?new=1`);
    } catch (e: any) {
      setErr(e.message);
      push(e.message, "error");
    } finally {
      setBusy(false);
    }
  };

  if (items.length === 0) {
    nav("/cart");
    return null;
  }

  return (
    <main id="main-content" className="container checkout-grid" style={{ padding: "var(--s-8) var(--s-5)", display: "grid", gridTemplateColumns: "1fr 340px", gap: "var(--s-8)" }}>
      <form id="checkout-form" onSubmit={submit} className="stack">
        <div>
          <p className="eyebrow" style={{ marginBottom: 4 }}>Checkout</p>
          <h1 className="h-title">주문서 작성</h1>
        </div>

        <section className="card" style={{ padding: 24 }}>
          <h3 className="h-section">수령 방식</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {(["delivery", "pickup"] as const).map((f) => (
              <button key={f} type="button" onClick={() => { setFulfillment(f); setShippingOverride(null); }}
                className="card" style={{
                  padding: 16, textAlign: "left", cursor: "pointer",
                  borderColor: fulfillment === f ? "var(--accent)" : "var(--border)",
                  background: fulfillment === f ? "var(--accent-subtle)" : "var(--surface)",
                }}>
                <div style={{ fontWeight: 600 }}>{f === "delivery" ? "🚚 배송" : "🏪 현장/매장 수령"}</div>
                <div className="muted" style={{ fontSize: 15, marginTop: 4 }}>
                  {f === "delivery" ? "사업장 또는 공사 현장으로 배송" : "매장 방문 또는 현장 지정 수령"}
                </div>
              </button>
            ))}
          </div>
        </section>

        {fulfillment === "delivery" ? (
          <section className="card" style={{ padding: 24 }}>
            <h3 className="h-section">배송 정보</h3>
            <div className="stack">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <label><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>받는 분 *</div>
                  <input required value={form.ship_name} onChange={(e) => setForm({ ...form, ship_name: e.target.value })} />
                </label>
                <label><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>연락처 *</div>
                  <input required value={form.ship_phone} onChange={(e) => setForm({ ...form, ship_phone: e.target.value })} />
                </label>
              </div>
              <label><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>배송 주소 *</div>
                <input required value={form.ship_address} onChange={(e) => setForm({ ...form, ship_address: e.target.value })} />
              </label>
              <label><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>배송 메모</div>
                <input placeholder="예) 경비실 맡겨주세요" value={form.ship_memo} onChange={(e) => setForm({ ...form, ship_memo: e.target.value })} />
              </label>
            </div>
          </section>
        ) : (
          <section className="card" style={{ padding: 24 }}>
            <h3 className="h-section">수령 정보</h3>
            <div className="stack">
              <label><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>수령 장소</div>
                <input placeholder="매장 방문 / 현장 주소 입력" value={form.pickup_site} onChange={(e) => setForm({ ...form, pickup_site: e.target.value })} />
              </label>
              <label><div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>수령 희망일</div>
                <input type="date" value={form.pickup_date} onChange={(e) => setForm({ ...form, pickup_date: e.target.value })} />
              </label>
            </div>
          </section>
        )}

        <section className="card" style={{ padding: 24 }}>
          <h3 className="h-section">결제 수단</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            {([
              { k: "card", l: "신용/체크카드", d: "즉시 결제" },
              { k: "bank_transfer", l: "계좌이체", d: "주문 후 24시간 내 입금" },
              { k: "tax_invoice", l: "세금계산서 후불", d: "승인된 정규 거래처" },
            ] as const).map((o) => (
              <button key={o.k} type="button" onClick={() => setPayment(o.k)}
                className="card" style={{
                  padding: 14, textAlign: "left", cursor: "pointer",
                  borderColor: payment === o.k ? "var(--accent)" : "var(--border)",
                  background: payment === o.k ? "var(--accent-subtle)" : "var(--surface)",
                }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{o.l}</div>
                <div className="muted" style={{ fontSize: 15, marginTop: 2 }}>{o.d}</div>
              </button>
            ))}
          </div>
          <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 16, cursor: "pointer" }}>
            <input type="checkbox" style={{ width: "auto" }} checked={taxInvoice} onChange={(e) => setTaxInvoice(e.target.checked)} />
            <span style={{ fontSize: 15 }}>세금계산서 발행 요청</span>
          </label>
        </section>

        {err && <div style={{ fontSize: 15, color: "var(--danger)", padding: "10px 14px", background: "#fce1dc", borderRadius: 4 }}>{err}</div>}
      </form>

      <aside>
        <div className="card" style={{ padding: 20, position: "sticky", top: 80 }}>
          <h3 className="h-section">주문 요약</h3>
          <div className="stack" style={{ gap: 6, marginBottom: 12 }}>
            {items.map((it) => (
              <div key={it.product.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 15 }}>
                <span className="muted" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginRight: 8 }}>
                  {it.product.name} × {it.quantity}
                </span>
                <span style={{ fontWeight: 500, whiteSpace: "nowrap" }}>{fmt(it.product.price * it.quantity)}원</span>
              </div>
            ))}
          </div>
          <div style={{ borderTop: "1px solid var(--border)", paddingTop: 12 }} className="stack">
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15 }}>
              <span className="muted">상품 합계</span><span>{fmt(subtotal)}원</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 15 }}>
              <span className="muted">{fulfillment === "delivery" ? "배송비" : "현장 수령"}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input type="number" min={0} step={1000} value={shipping}
                  onChange={(e) => setShippingOverride(Math.max(0, Number(e.target.value) || 0))}
                  style={{ width: 100, textAlign: "right", padding: "4px 8px", fontSize: 14 }} />
                <span>원</span>
              </div>
            </div>
            {shippingOverride !== null && shippingOverride !== defaultShipping && (
              <button type="button" className="btn btn-ghost btn-sm" style={{ alignSelf: "flex-end", padding: "2px 8px" }}
                onClick={() => setShippingOverride(null)}>
                기본값 복원 ({defaultShipping === 0 ? "무료" : `${fmt(defaultShipping)}원`})
              </button>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", paddingTop: 10, borderTop: "1px solid var(--border)" }}>
              <span style={{ fontWeight: 600 }}>총 결제</span>
              <span style={{ fontSize: 22, fontWeight: 700, color: "var(--accent)" }}>{fmt(total)}원</span>
            </div>
          </div>
          <button type="submit" form="checkout-form" className="btn btn-cta" style={{ width: "100%", marginTop: 16 }} disabled={busy}>
            {busy ? "주문 접수 중..." : `${fmt(total)}원 결제하기`}
          </button>
        </div>
      </aside>
      <style>{`
        @media (max-width: 860px) {
          .checkout-grid { grid-template-columns: 1fr !important; gap: var(--s-5) !important; }
          .checkout-grid aside > div { position: static !important; }
        }
        @media (max-width: 540px) {
          .checkout-grid form section > div[style*="1fr 1fr"],
          .checkout-grid form section > div[style*="repeat(3"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </main>
  );
}
